import { Box } from '@mui/material';
import BottomGreenBar from 'components/bottom-green-bar';
import { CompanyAvailabilityFilterType } from 'constants/CompanyAvailabilityFilterType';
import { t } from 'i18next';
import { toNumber } from 'lodash';
import AddContractModal from 'modules/contracts/modals/add/AddContractModal';
import { IDayProps } from 'modules/contracts/types';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getCompanyAvailabilityForWeekAction,
  resetAvailabilityForWeekAction,
} from 'store/actions/availabilities-actions';
import {
  resetCompanyAvailabilityFiltersAction,
  setCompanyAvailabilityFiltersAction,
} from 'store/actions/company-availability-filters-actions';
import { getDefaultCompanyBreakTimeAction } from 'store/actions/company-actions';
import { defaultCompanyBreakSelector } from 'store/selectors/company-selectors';
import { companyAvailabilityFiltersSelector } from 'store/selectors/company-availability-filters-selector';
import { CONTRACTS__NEW_CONTRACT } from 'translations/constants';
import { ReactComponent as NewContractIcon } from '../../../assets/icons/CircleAddNewContractIcon.svg';
import CompanyAvailabilityTable from './table';
import { CompanyAvailabilityProps } from './types';

const CompanyAvailability = (props: CompanyAvailabilityProps): JSX.Element => {
  const { week, setWeek, currentWeek, companyId, disableCreateButtons, setYear } = props;

  const dispatch = useAppDispatch();

  const companyAvailabilityFilters = useAppSelector(companyAvailabilityFiltersSelector);
  const defaultBreakTime = useAppSelector(defaultCompanyBreakSelector);

  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  const [numberOfPages, setNumberOfPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [days, setDays] = useState<IDayProps[]>([]);
  const [showBottomGreenBar, setShowBottomGreenBar] = useState<boolean>(false);
  const [addContractModalOpen, setAddContractModalOpen] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<number>(null);
  const [isContractSaved, setIsContractSaved] = useState<boolean>(false);
  const [areAllCardsUnhovered, setAreAllCardsUnhovered] = useState<boolean>(false);
  // The day auto-selected when opening the modal from a single cell's "Add planning" icon. It is
  // tracked separately so that cancelling the modal only clears this auto-selection and leaves a
  // manual (green-bar) multi-day selection untouched.
  const [autoSelectedDay, setAutoSelectedDay] = useState<number>(null);

  const daysChecked = useMemo(() => days.filter(day => day.checked).length, [days]);

  const bottomGreenBarActions = [
    {
      content: t(CONTRACTS__NEW_CONTRACT),
      icon: <NewContractIcon />,
    },
  ];

  useEffect(() => {
    dispatch(resetCompanyAvailabilityFiltersAction());
  }, [dispatch]);

  useEffect(() => {
    if (!companyId) {
      dispatch(resetAvailabilityForWeekAction());
      return;
    }
    dispatch(getCompanyAvailabilityForWeekAction(companyAvailabilityFilters));
    dispatch(getDefaultCompanyBreakTimeAction(toNumber(companyId)));
  }, [dispatch, companyAvailabilityFilters, companyId]);

  useEffect(() => {
    dispatch(
      setCompanyAvailabilityFiltersAction([
        {
          filterType: CompanyAvailabilityFilterType.PAGE,
          value: toNumber(currentPage),
        },
      ]),
    );
  }, [currentPage, dispatch]);

  useEffect(() => {
    dispatch(
      setCompanyAvailabilityFiltersAction([
        {
          filterType: CompanyAvailabilityFilterType.PAGE_SIZE,
          value: toNumber(itemsPerPage),
        },
      ]),
    );
    setCurrentPage(1);
  }, [itemsPerPage, dispatch]);

  useEffect(() => {
    if (daysChecked > 0) {
      setShowBottomGreenBar(true);
      disableCreateButtons(true);
    } else {
      setShowBottomGreenBar(false);
      disableCreateButtons(false);
    }
  }, [daysChecked, disableCreateButtons]);

  useEffect(() => {
    if (isContractSaved) {
      setDays(days.map(d => ({ ...d, checked: false })));
      setAutoSelectedDay(null);
      setIsContractSaved(false);
    }
  }, [isContractSaved, days]);

  useEffect(() => {
    if (areAllCardsUnhovered) {
      setTimeout(() => {
        setAreAllCardsUnhovered(false);
      }, 1000);
    }
  }, [areAllCardsUnhovered]);

  // After a contract is created we refetch the week so the contract-count indicator reflects the
  // current database state. The thunk surfaces its own error state, so a failed refresh falls back to
  // the existing generic availabilities error handling without affecting the already-persisted contract.
  const onContractCreation = () => {
    dispatch(getCompanyAvailabilityForWeekAction(companyAvailabilityFilters));
    setAddContractModalOpen(false);
    setAreAllCardsUnhovered(true);
    setSelectedEmployee(null);
  };

  // Closing/cancelling the modal must not leave an icon-selected day highlighted. Only the
  // auto-selected day is cleared; a manual multi-day selection made via the green bar is preserved.
  const handleModalClose = () => {
    setAddContractModalOpen(false);
    if (autoSelectedDay !== null) {
      const newDays = days.map(d => (d.date.getDate() === autoSelectedDay ? { ...d, checked: false } : d));
      setDays(newDays);
      setAutoSelectedDay(null);
      if (!newDays.some(d => d.checked)) {
        setSelectedEmployee(null);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <CompanyAvailabilityTable
        companyId={companyId}
        week={week}
        setWeek={setWeek}
        setYear={setYear}
        currentWeek={currentWeek}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        numberOfPages={numberOfPages}
        setNumberOfPages={setNumberOfPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        days={days}
        setDays={setDays}
        selectedEmployee={selectedEmployee}
        setSelectedEmployee={setSelectedEmployee}
        setAddContractModalOpen={setAddContractModalOpen}
        setContractSaved={setIsContractSaved}
        setAutoSelectedDay={setAutoSelectedDay}
        areAllCardsUnhovered={areAllCardsUnhovered}
      />
      {showBottomGreenBar && !addContractModalOpen && (
        <BottomGreenBar
          numberOfItems={daysChecked}
          actions={bottomGreenBarActions}
          onClearAll={() => {
            setDays(days.map(d => ({ ...d, checked: false })));
          }}
          onCopy={() => {
            setAddContractModalOpen(true);
          }}
        />
      )}
      {addContractModalOpen && (
        <AddContractModal
          onClose={handleModalClose}
          onSuccess={onContractCreation}
          companyId={companyId}
          week={currentWeek}
          prefilledEmployeeId={selectedEmployee}
          prefilledDates={days.filter(d => d.checked).map(d => d.date.getDate())}
          setSavedContract={setIsContractSaved}
          weekDate={week}
          defaultBreakTime={defaultBreakTime}
        />
      )}
    </Box>
  );
};

export default CompanyAvailability;
